import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { Alert, Box, Button, Snackbar, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { BackIcon, ConfirmIcon, LeftArrowIcon } from 'src/v3/core/icons'
import axios, { pluckError } from 'src/utilities/http-client'
import { QuizBuilder } from 'src/v3/components/quiz/quiz-builder'
import Loading from 'src/v3/components/loading'
import {
  fetchEvents,
  fetchQuiz,
  fetchQuizQuestions,
  selectActiveEvent,
  selectEventById,
  selectEventsStatus,
  selectQuestionsByQuiz,
  selectQuestionsStatusByQuiz,
  selectQuizByEvent,
  selectQuizStatusByEvent,
} from './events.slice'
import { EventWizardStep } from './types'

let Quiz = ({
  setStep,
}: {
  setStep: React.Dispatch<React.SetStateAction<EventWizardStep>>
}): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { eventId } = useParams()

  let event = eventId
    ? useAppSelector(selectEventById(eventId))
    : useAppSelector(selectActiveEvent)
  let eventsStatus = useAppSelector(selectEventsStatus)
  let eventID = event?.id || ''

  let quiz = useAppSelector(selectQuizByEvent(eventID))
  let quizStatus = useAppSelector(selectQuizStatusByEvent(eventID))
  let quizID = quiz?.id || ''
  let questions = useAppSelector(selectQuestionsByQuiz(quizID))
  let questionsStatus = useAppSelector(selectQuestionsStatusByQuiz(quizID))
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  useEffect(() => {
    if (eventsStatus === 'uninitialized') {
      dispatch(fetchEvents())
    }
  }, [eventsStatus])

  useEffect(() => {
    if (eventID && (!quizStatus || quizStatus === 'uninitialized')) {
      dispatch(fetchQuiz(eventID))
    }
  }, [quizStatus, eventID])

  useEffect(() => {
    if (quizID && (!questionsStatus || questionsStatus === 'uninitialized')) {
      dispatch(fetchQuizQuestions(quizID))
    }
  }, [questionsStatus, quizID])

  let buttonBar = (
    <Box
      sx={{
        mt: 5,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 1,
      }}
    >
      <Button
        sx={{ color: 'text.primary' }}
        startIcon={<LeftArrowIcon />}
        onClick={() => setStep('sessions')}
      >
        Previous: Sessions
      </Button>
      {quiz && (
        <>
          <Box sx={{ flexGrow: 1 }}></Box>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            onClick={() => navigate('/events')}
          >
            Save & Exit
          </Button>
          <Button type="submit" variant="contained" endIcon={<ConfirmIcon />}>
            Save Changes
          </Button>
        </>
      )}
    </Box>
  )

  return (
    <Box>
      {/* <Typography variant="h5" sx={{ mb: 3 }}>
        Event Quiz
      </Typography> */}
      {!quiz ? (
        <Box>
          <Typography variant="body1" sx={{ mb: 1 }}>
            If you would like for your event to have a quiz, click the button
            below to begin building one for this event's attendees to take at
            its conclusion.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              color="secondary"
              loading={loading}
              onClick={() => {
                setLoading(true)
                axios
                  .post('/api/event/quiz', {
                    eventID,
                    passingScore: 0,
                    displayedQuestions: 0,
                    randomizeQuestions: true,
                  })
                  .then(() => dispatch(fetchQuiz(eventID)))
                  .catch((err) =>
                    setErrorMessage(pluckError(err, `Quiz creation failed`)),
                  )
                  .finally(() => setLoading(false))
              }}
            >
              Create Quiz
            </Button>
            <Button
              variant="outlined"
              startIcon={<BackIcon />}
              onClick={() => navigate('/events')}
            >
              Return to Events
            </Button>
          </Box>
          {buttonBar}
        </Box>
      ) : (
        <QuizBuilder
          quiz={quiz}
          fetchQuiz={fetchQuiz} // keyed by eventID
          fetchQuizBy={eventID}
          questionData={questions}
          fetchQuestionData={fetchQuizQuestions} // keyed by quizID, not eventID
          fetchQuestionDataBy={quizID}
          apiPath="/api/event"
          submitButton={buttonBar}
        />
      )}
      <Loading entities={[eventsStatus, quizStatus, questionsStatus]} />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Quiz
